/**
 * Shared login page (trainer + admin + case).
 * Trainer signup linked separately. Case sign-in initially via /join/:token.
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth.js";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState<string | null>(null);
  const [busy, setBusy]         = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      const next = (location.state as { from?: string })?.from
        ?? (u.role === "case" ? "/me"
        : u.role === "admin"  ? "/admin/approvals"
        : "/dashboard");
      nav(next, { replace: true });
    } catch (err: any) {
      const code = err?.body?.error;
      setError(
        code === "invalid_credentials" ? "Email 或密碼錯誤"
        : code === "pending_approval"  ? "帳號等候管理員審核中"
        : code === "missing_fields"    ? "請填 email + 密碼"
        : err?.message ?? "登入失敗，稍後再試"
      );
    } finally { setBusy(false); }
  }

  return (
    <main className="page page--auth">
      <div className="rise rise-1" style={{ marginBottom: "var(--s-7)" }}>
        <div className="serial" style={{ marginBottom: "var(--s-3)" }}>VOL 01 · ISSUE 08</div>
        <h1 style={{ fontSize: "var(--t-2xl)", marginBottom: "var(--s-3)" }}>
          <span className="italic-display" style={{ color: "var(--rust)" }}>登入</span>{" "}CogniHome
        </h1>
        <p className="muted" style={{ fontSize: "var(--t-sm)", lineHeight: 1.6 }}>
          訓練師工作台、個案訓練紀錄、管理員審核 — 同一入口。<br />
          個案首次加入請透過訓練師寄出的 join 連結。
        </p>
      </div>

      <form onSubmit={onSubmit} className="rise rise-2">
        <div className="col" style={{ gap: "var(--s-4)" }}>
          <div className="field">
            <label className="field-label">Email</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <div className="field">
            <label className="field-label">密碼</label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="—"
            />
          </div>

          {error && <div className="alert">{error}</div>}

          <button type="submit" className="btn btn--rust" disabled={busy} style={{ justifyContent: "center" }}>
            {busy ? "登入中…" : "登入 →"}
          </button>
        </div>
      </form>

      <div className="rise rise-3" style={{ marginTop: "var(--s-7)", fontSize: "var(--t-sm)" }}>
        <p className="muted" style={{ marginBottom: "var(--s-2)" }}>
          訓練師還沒帳號？<Link to="/signup">自助申請</Link>（送審通過後可登入）
        </p>
        <p className="muted" style={{ fontSize: "var(--t-xs)" }}>
          這不是醫療診斷或治療系統。
        </p>
      </div>
    </main>
  );
}
